import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { MerchantService } from '../auth/merchant.service';
import { BusinessAccountDto, CreateBusinessAccountRequest } from '../models/api.models';
import { KlidiApi } from './api.service';

export type AccountType = 'Individual' | 'Business';

const ACCOUNT_TYPE_KEY = 'klidi_account_type';
const SELECTED_BUSINESS_KEY = 'klidi_selected_business';

/**
 * Top-level sections that only belong in the navigation of a business account. /administration
 * manages a shop's team, and only a business account has shops.
 */
const BUSINESS_SECTIONS = new Set(['business', 'compliance', 'administration']);

/** All an account waiting for its application's approval can open: the application, and help. */
export const AWAITING_APPROVAL_SECTIONS = new Set(['application', 'help-support']);

/**
 * Which account the app is currently acting as.
 *
 * A merchant signs in once but can own several business accounts. Each business account is
 * its own merchant row server-side — separate balances, transactions and Fireblocks deposit
 * addresses — so "switching business" means switching the merchant id every API call uses.
 * That id is {@link activeMerchantId}; components must read it from here rather than from
 * AuthService, which only ever knows the signed-in merchant.
 */
@Injectable({ providedIn: 'root' })
export class MerchantContextService {
  private api = inject(KlidiApi);
  private auth = inject(AuthService);
  private merchants = inject(MerchantService);

  readonly accountType = signal<AccountType>(readAccountType());
  readonly businesses = signal<BusinessAccountDto[]>([]);
  readonly selectedBusinessId = signal<string | null>(readSelectedBusinessId());

  /** True while the business list is in flight; false once it has resolved at least once. */
  readonly loading = signal(false);
  readonly loaded = signal(false);
  readonly error = signal<string | null>(null);

  readonly selectedBusiness = computed(
    () => this.businesses().find(b => b.id === this.selectedBusinessId()) ?? null
  );

  readonly hasBusinesses = computed(() => this.businesses().length > 0);

  /** The merchant has switched to Business but hasn't created one yet — show the CTA. */
  readonly needsFirstBusiness = computed(
    () => this.accountType() === 'Business' && this.loaded() && !this.hasBusinesses()
  );

  /**
   * Merchant id every data call should use. Null means "nothing to load yet": either we're
   * signed out, or we're in Business mode with no business account to act as.
   */
  readonly activeMerchantId = computed<number | null>(() => {
    if (this.accountType() === 'Individual') return this.auth.merchantId();

    const business = this.selectedBusiness();
    return business ? Number(business.id) : null;
  });

  /** The active account can't move money until its KYB is approved. */
  readonly canTransact = computed(() => {
    if (this.accountType() === 'Individual') return true;
    return this.selectedBusiness()?.canTransact ?? false;
  });

  /**
   * Whether a top-level section belongs in the navigation for the active account. Individual has no
   * Business or Compliance section. This only hides them: their pages stay routable, because some
   * flows link into them on purpose (the dashboard's "Start verification").
   * Reads {@link accountType}, so calling it inside a computed tracks the toggle.
   *
   * Until the application is approved, only the application and help are shown at all
   * (authGuard turns the other pages away too).
   */
  showsSection(section: string): boolean {
    if (this.merchants.awaitingApproval()) return AWAITING_APPROVAL_SECTIONS.has(section);
    return this.accountType() === 'Business' || !BUSINESS_SECTIONS.has(section);
  }

  private lastLoadedFor: number | null = null;

  constructor() {
    // (Re)load the business list whenever the signed-in merchant changes.
    effect(() => {
      const ownerId = this.auth.merchantId();
      if (ownerId === null) {
        this.lastLoadedFor = null;
        this.businesses.set([]);
        this.loaded.set(false);
        return;
      }
      if (ownerId === this.lastLoadedFor) return;
      this.lastLoadedFor = ownerId;
      this.loadBusinesses();
    }, { allowSignalWrites: true });

    // Persist the context so a page refresh doesn't silently drop the user back into
    // their personal account while the header still says otherwise.
    effect(() => localStorage.setItem(ACCOUNT_TYPE_KEY, this.accountType()));
    effect(() => {
      const id = this.selectedBusinessId();
      if (id) localStorage.setItem(SELECTED_BUSINESS_KEY, id);
      else localStorage.removeItem(SELECTED_BUSINESS_KEY);
    });
  }

  setAccountType(type: AccountType): void {
    this.accountType.set(type);
    // Coming back to Business with a stale/empty selection: fall back to the first account.
    if (type === 'Business' && !this.selectedBusiness() && this.hasBusinesses()) {
      this.selectedBusinessId.set(this.businesses()[0].id);
    }
  }

  selectBusiness(business: BusinessAccountDto | string): void {
    this.selectedBusinessId.set(typeof business === 'string' ? business : business.id);
    this.accountType.set('Business');
  }

  loadBusinesses(): void {
    this.loading.set(true);
    this.error.set(null);

    this.api.businesses().subscribe({
      next: rows => {
        this.businesses.set(rows);
        this.reconcileSelection(rows);
        this.loading.set(false);
        this.loaded.set(true);
      },
      error: () => {
        this.businesses.set([]);
        this.selectedBusinessId.set(null);
        this.error.set('Could not load your business accounts.');
        this.loading.set(false);
        this.loaded.set(true);
      }
    });
  }

  /** Create a business account and switch straight into it. */
  createBusiness(req: CreateBusinessAccountRequest): Observable<BusinessAccountDto> {
    return this.api.createBusiness(req).pipe(
      tap(created => {
        this.businesses.update(list => [...list, created]);
        this.selectedBusinessId.set(created.id);
        this.accountType.set('Business');
        this.loaded.set(true);
      })
    );
  }

  /** Keep the stored selection honest: drop ids that no longer exist, default to the first. */
  private reconcileSelection(rows: BusinessAccountDto[]): void {
    if (rows.length === 0) {
      this.selectedBusinessId.set(null);
      return;
    }
    const current = this.selectedBusinessId();
    if (!current || !rows.some(r => r.id === current)) {
      this.selectedBusinessId.set(rows[0].id);
    }
  }
}

function readAccountType(): AccountType {
  return localStorage.getItem(ACCOUNT_TYPE_KEY) === 'Business' ? 'Business' : 'Individual';
}

function readSelectedBusinessId(): string | null {
  return localStorage.getItem(SELECTED_BUSINESS_KEY);
}
